class FavoritesUrlManager {
  // Base62 alphabet: digits + lowercase + uppercase. No `_` or `~` so it can't collide with our separators.
  static BASE62 = '0123456789abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ';

  // BigInt-based to stay safe if Shopify IDs ever exceed Number.MAX_SAFE_INTEGER.
  static idToBase62(id) {
    let n = BigInt(id);
    if (n === 0n) return '0';
    let s = '';
    while (n > 0n) {
      s = this.BASE62[Number(n % 62n)] + s;
      n /= 62n;
    }
    return s;
  }

  static idFromBase62(str) {
    let n = 0n;
    for (const c of str) {
      const idx = this.BASE62.indexOf(c);
      if (idx === -1) return null;
      n = n * 62n + BigInt(idx);
    }
    return n.toString();
  }

  // Format: base62Id[~encodedSize] joined by _, e.g. "2WqLqMr_2VtBd7q~L_2WaCeJk~38.5%20EU".
  // variantId is omitted — applyStoredSizes() resolves it from the size dropdown at render time.
  // Size is URI-encoded, then `~` and `_` are escaped manually since encodeURIComponent leaves them literal.
  static encodeFavorites(favorites) {
    return favorites
      .map((f) => {
        const id = this.idToBase62(f.id);
        if (!f.size) return id;
        const safeSize = encodeURIComponent(f.size)
          .replace(/~/g, '%7E')
          .replace(/_/g, '%5F');
        return `${id}~${safeSize}`;
      })
      .join('_');
  }

  static decodeFavorites(str) {
    if (!str) return [];
    return str.split('_').map((part) => {
      const tildeIdx = part.indexOf('~');
      const encodedId = tildeIdx === -1 ? part : part.slice(0, tildeIdx);
      const id = this.idFromBase62(encodedId);
      if (id === null) return null;
      return {
        id,
        size: tildeIdx === -1 ? null : decodeURIComponent(part.slice(tildeIdx + 1)),
        variantId: null,
      };
    }).filter((item) => item !== null);
  }

  static getFavoritesFromUrl() {
    // Read raw search to preserve literal `_` and `~` separators — URLSearchParams.get would decode percent-escapes prematurely.
    const match = window.location.search.match(/[?&]favorites=([^&#]*)/);
    if (!match) return [];
    return this.decodeFavorites(match[1]);
  }

  static updateUrl(favorites) {
    const url = new URL(window.location);
    const params = new URLSearchParams(url.search);
    params.delete('favorites');

    if (favorites.length === 0) {
      url.search = params.toString();
      window.history.replaceState({}, '', url);
      return;
    }

    // Construct search manually: URLSearchParams.set would encode `~` to `%7E`, defeating the compact format.
    const encoded = this.encodeFavorites(favorites);
    const otherParams = params.toString();
    url.search = (otherParams ? `${otherParams}&` : '') + `favorites=${encoded}`;
    window.history.replaceState({}, '', url);
  }

  static clearSearchParameters() {
    const url = new URL(window.location.href);
    url.search = '';
    window.history.replaceState({}, '', url.href);
  }
}

class Favorites extends HTMLElement {
  connectedCallback() {
    if (this._initialized) return;
    this._initialized = true;

    this.element = this;
    this.delegateElement = new Delegate(this.element);
    this.delegateBody = new Delegate(document.body);

    this.cache = new Map();
    this.cacheTimeout = 5 * 60 * 1000; // 5 minutes cache timeout
    this.cachePrefix = 'favorites_';

    this.options = JSON.parse(
      this.element.getAttribute('data-section-settings')
    );

    this.favoritesHeader = document.querySelector('favorites-header');
    this.gridElement = this.element.querySelector('.favorites__grid');
    this.shareButton = this.element.querySelector(
      '.favorites__share-button-container'
    );
    this.loadMoreWrapper = this.element.querySelector(
      '.favorites__load-more-wrapper'
    );
    this.loadMoreButton = this.element.querySelector('.favorites__load-more');
    this.spinningLoader = this.element.querySelector('.favorites__grid-container .spinningLoader');

    this.visibleCount = 0;
    this.batchSize = this.options.productsPerPage || 8;
    this.disableShowingAddedProducts = false;
    this.favoritesEmpty = this.element.querySelector('.favorites__empty');

    this._portaledSnackbars = Array.from(this.element.querySelectorAll('.favorites__snackbar'));
    this._portaledSnackbars.forEach((snackbar) => document.body.appendChild(snackbar));

    this.renderContent();
    this.bindEvents();
    new AddToCart(this.element);
  }

  bindEvents() {
    if (!this.disableShowingAddedProducts) {
      this.delegateBody.on('favorite:remove', this.removeFavorite.bind(this));
      this.delegateBody.on('favorite:add-external', this.handleExternalAdd.bind(this));
    }

    this.delegateElement.on(
      'click',
      '[data-action="copy-link"]',
      this.copyUrlToClipboard.bind(this)
    );

    this.delegateElement.on(
      'click',
      '[data-action="load-more-favorites"]',
      this.handleLoadMore.bind(this)
    );

    this.delegateElement.on(
      'click',
      '[data-action="favorites-size-select"]',
      this.handleFavoritesSizeSelect.bind(this)
    );

    this.delegateElement.on(
      'click',
      '.pc-favorites__size-dropdown-button',
      this.closeOtherDropdowns.bind(this)
    );

    this.delegateBody.on('click', (event) => {
      if (event.target.closest('.pc-favorites__size-dropdown')) return;
      this.closeAllDropdowns();
    });

    this.delegateBody.on('keydown', (event) => {
      if (event.key !== 'Escape') return;
      const openTrigger = this.gridElement.querySelector(
        '.pc-favorites__size-dropdown-button[aria-expanded="true"]'
      );
      if (!openTrigger) return;
      this.closeAllDropdowns();
      openTrigger.focus();
    });

    this._onBeforeUnload = FavoritesUrlManager.clearSearchParameters.bind(FavoritesUrlManager);
    // delegate-exempt: beforeunload is window-scoped and does not bubble to body, so Delegate cannot carry it. Cleanup in disconnectedCallback.
    window.addEventListener('beforeunload', this._onBeforeUnload);
  }

  disconnectedCallback() {
    if (this._onBeforeUnload) {
      window.removeEventListener('beforeunload', this._onBeforeUnload);
      this._onBeforeUnload = null;
    }
    if (this._snackbarRegisterRaf) {
      cancelAnimationFrame(this._snackbarRegisterRaf);
      this._snackbarRegisterRaf = null;
    }
    if (this._snackbarBodyClickHandler) {
      this.delegateBody?.off('click', this._snackbarBodyClickHandler);
      this._snackbarBodyClickHandler = null;
    }
    if (this.snackbarTimeout) {
      clearTimeout(this.snackbarTimeout);
      this.snackbarTimeout = null;
    }
    if (this.removeFavoriteTimeout) {
      clearTimeout(this.removeFavoriteTimeout);
      this.removeFavoriteTimeout = null;
    }
    if (this._sizeChangeTimeout) {
      clearTimeout(this._sizeChangeTimeout);
      this._sizeChangeTimeout = null;
    }
    if (this._heightUnlockTimeout) {
      clearTimeout(this._heightUnlockTimeout);
      this._heightUnlockTimeout = null;
    }
    if (this._activeRequestController) {
      this._activeRequestController.abort();
      this._activeRequestController = null;
    }
    if (this._portaledSnackbars) {
      this._portaledSnackbars.forEach((snackbar) => snackbar.remove());
      this._portaledSnackbars = null;
    }
    this.delegateElement?.destroy();
    this.delegateBody?.destroy();
    this.cache?.clear();
  }

  renderContent() {
    this.compareFavorites();

    const localFavorites = this.getFavorites();
    const favoritesTotal = this.getFavoritesToShow().length;

    if (favoritesTotal > 0) {
      this.loadInitial();

      if (!this.disableShowingAddedProducts && localFavorites.length > 0) {
        this.updateUrl();
      }
    } else {
      if (this.favoritesEmpty) {
        this.favoritesEmpty.classList.remove('is-hidden');
        this.favoritesEmpty.ariaHidden = 'false';
      }
    }
  }

  checkIfEmpty() {
    const items = this.getFavoritesToShow();

    if (items.length === 0) {
      this.gridElement.parentElement.classList.add('is-hidden');
      if (this.shareButton) {
        this.shareButton.classList.add('is-hidden');
        this.shareButton.ariaHidden = 'true';
      }
      if (this.loadMoreWrapper) {
        this.loadMoreWrapper.classList.add('is-hidden');
        this.loadMoreWrapper.setAttribute('aria-hidden', 'true');
      }
      this.favoritesEmpty.classList.remove('is-hidden');
    } else {
      this.gridElement.parentElement.classList.remove('is-hidden');
      if (this.shareButton) {
        this.shareButton.classList.remove('is-hidden');
        this.shareButton.ariaHidden = 'false';
      }
      this.updateLoadMoreButton();
    }
  }

  cacheKey(startIndex, endIndex, favorites) {
    const favoritesHash = this.hashFavorites(favorites);
    return `${this.cachePrefix}${favoritesHash}_${startIndex}_${endIndex}`;
  }

  hashFavorites(favorites) {
    return btoa(JSON.stringify(favorites.map(f => f.id).sort())).slice(0, 16);
  }

  getCachedData(startIndex, endIndex, favorites) {
    const key = this.cacheKey(startIndex, endIndex, favorites);
    const cached = this.cache.get(key);

    if (cached && Date.now() - cached.timestamp < this.cacheTimeout) {
      return cached.data;
    }

    if (cached) {
      this.cache.delete(key);
    }

    return null;
  }

  setCachedData(startIndex, endIndex, favorites, data) {
    const key = this.cacheKey(startIndex, endIndex, favorites);
    this.cache.set(key, {
      data: data,
      timestamp: Date.now()
    });
  }

  clearCache() {
    this.cache.clear();
  }

  loadInitial() {
    this.spinningLoader.classList.add('is-visible');
    const total = this.getFavoritesToShow().length;
    const end = Math.min(this.batchSize, total);
    this.visibleCount = end;
    this.rerenderFavorites(0, end, true);
    this.updateLoadMoreButton();
    this.checkIfEmpty();
  }

  closeOtherDropdowns(e, target) {
    const clickedButton = target || e.target.closest('.pc-favorites__size-dropdown-button');
    if (!clickedButton) return;
    const currentDropdown = clickedButton.closest('.pc-favorites__size-dropdown');
    this.closeAllDropdowns(currentDropdown);
  }

  closeAllDropdowns(except = null) {
    this.gridElement
      .querySelectorAll('.pc-favorites__size-dropdown-button[aria-expanded="true"]')
      .forEach((btn) => {
        const dropdown = btn.closest('.pc-favorites__size-dropdown');
        if (!dropdown || dropdown === except) return;
        btn.setAttribute('aria-expanded', 'false');
        const content = dropdown.querySelector('.pc-favorites__size-dropdown-content');
        if (content) content.setAttribute('aria-hidden', 'true');
      });
  }

  _applySizeToCard(productCard, option, { animate }) {
    const dropdown = productCard.querySelector('.pc-favorites__size-dropdown');
    const triggerText = dropdown?.querySelector('.pc-favorites__size-dropdown-button-text');
    const atbButton = productCard.querySelector('.pc-favorites__atb-button');
    const heartIcon = productCard.querySelector('[data-action="toggle-favorites"]');

    const size = option.dataset.size;
    const variantId = option.dataset.variantId;
    const isAvailable = option.dataset.available !== 'false';

    if (triggerText) {
      const setLabel = () => {
        triggerText.textContent = size;
        triggerText.classList.toggle('is-unavailable', !isAvailable);
      };
      if (animate) {
        triggerText.classList.add('is-changing');
        // timeout: matches the 180ms .is-changing fade-out so the label swaps mid-fade. Cleared in disconnectedCallback.
        this._sizeChangeTimeout = setTimeout(() => {
          setLabel();
          triggerText.classList.remove('is-changing');
        }, 180);
      } else {
        setLabel();
      }
    }

    dropdown?.querySelectorAll('.pc-favorites__size-dropdown-item.is-selected').forEach(el => {
      el.classList.remove('is-selected');
      el.setAttribute('aria-selected', 'false');
    });
    option.classList.add('is-selected');
    option.setAttribute('aria-selected', 'true');

    productCard.dataset.selectedVariantId = variantId;
    productCard.dataset.selectedSize = size;

    if (atbButton) {
      atbButton.dataset.variantId = variantId;
      atbButton.dataset.available = isAvailable ? 'true' : 'false';
      if (isAvailable) {
        atbButton.removeAttribute('disabled');
        atbButton.setAttribute('aria-disabled', 'false');
      } else {
        atbButton.setAttribute('disabled', '');
        atbButton.setAttribute('aria-disabled', 'true');
      }
    }

    if (heartIcon) {
      heartIcon.setAttribute('data-size', size);
      heartIcon.setAttribute('data-variant-id', variantId);
    }
  }

  handleFavoritesSizeSelect(e, target) {
    e.preventDefault();
    e.stopPropagation();

    const button = target || e.currentTarget || e.target.closest('[data-action="favorites-size-select"]');
    if (!button) return;

    const productCard = button.closest('product-card');
    if (!productCard) return;

    const dropdown = button.closest('.pc-favorites__size-dropdown');
    const triggerButton = dropdown?.querySelector('.pc-favorites__size-dropdown-button');
    const content = dropdown?.querySelector('.pc-favorites__size-dropdown-content');

    this._applySizeToCard(productCard, button, { animate: true });

    if (triggerButton) triggerButton.setAttribute('aria-expanded', 'false');
    if (content) content.setAttribute('aria-hidden', 'true');

    const productId = button.dataset.productId || productCard.dataset.productId;
    if (productId) {
      FavoritesStorage.updateFavoriteVariant(productId, button.dataset.size, button.dataset.variantId);
      this.clearCache();
    }
  }

  handleLoadMore(e) {
    e.preventDefault();
    const total = this.getFavoritesToShow().length;
    const end = Math.min(this.visibleCount + this.batchSize, total);
    const domCount = this.gridElement.querySelectorAll('product-card').length;
    const start = Math.min(domCount, this.visibleCount);
    if (start >= end) return;

    const buttonLoader = this.loadMoreButton?.querySelector('.itemLoader');
    if (buttonLoader) buttonLoader.classList.add('is-visible');
    if (this.loadMoreButton) this.loadMoreButton.disabled = true;

    this.visibleCount = end;
    this.rerenderFavorites(start, end, false);
    this.updateLoadMoreButton();
  }

  updateLoadMoreButton() {
    if (!this.loadMoreWrapper) return;
    const total = this.getFavoritesToShow().length;
    const hasMore = this.visibleCount < total;
    this.loadMoreWrapper.classList.toggle('is-hidden', !hasMore);
    this.loadMoreWrapper.setAttribute('aria-hidden', hasMore ? 'false' : 'true');
  }

  compareFavorites() {
    if (
      !(
        this.arraysEqual(this.getFavoritesFromUrl(), this.getFavorites()) ||
        this.getFavoritesFromUrl().length === 0
      )
    ) {
      this.disableShowingAddedProducts = true;
    }
  }

  arraysEqual(arr1, arr2) {
    if (arr1.length !== arr2.length) return false;
    const ids1 = new Set(arr1.map(f => f.id));
    return arr2.every(f => ids1.has(f.id));
  }

  updateUrl() {
    FavoritesUrlManager.updateUrl(this.getFavorites());
  }

  removeFavorite(e) {
    // timeout: 200ms delay lets the heart-toggle animation complete before the card is removed from the grid. Cleared in disconnectedCallback.
    this.removeFavoriteTimeout = setTimeout(() => {
      const productElement = this.element.querySelector(
        `[data-id="${e.detail.id}"]`
      );
      if (productElement) {
        productElement.closest('product-card')?.remove();
      }

      if (!this.disableShowingAddedProducts) {
        this.updateUrl();
      }

      this.clearCache();

      const total = this.getFavoritesToShow().length;
      this.visibleCount = Math.min(this.visibleCount, total);
      const domCount = this.gridElement.querySelectorAll('product-card').length;

      if (domCount < this.visibleCount) {
        this.rerenderFavorites(domCount, this.visibleCount, false);
      }

      this.checkIfEmpty();
      this.updateLoadMoreButton();
    }, 200);
  }

  toggleFavorites() {
    this.checkIfEmpty();
    this.favoritesHeader?.toggleFavorites();
  }

  handleExternalAdd() {
    this.clearCache();
    this.gridElement.innerHTML = '';
    this.visibleCount = 0;

    this.disableShowingAddedProducts = false;
    this.updateUrl();

    if (this.favoritesEmpty) {
      this.favoritesEmpty.classList.add('is-hidden');
      this.favoritesEmpty.ariaHidden = 'true';
    }

    this.renderContent();
  }

  getFavorites() {
    return FavoritesStorage.getFavorites();
  }

  getFavoritesFromUrl() {
    return FavoritesUrlManager.getFavoritesFromUrl();
  }

  getFavoritesToShow() {
    return this.disableShowingAddedProducts
      ? this.getFavoritesFromUrl()
      : this.getFavorites();
  }

  getSearchQueryStrings(startIndex, endIndex) {
    const items = this.getFavoritesToShow();
    const bucketSize = 10;
    const limitedItems = items.slice(startIndex, endIndex);

    const idBuckets = Array.from(
      { length: Math.ceil(limitedItems.length / bucketSize) },
      (_, i) =>
        limitedItems
          .slice(i * bucketSize, (i + 1) * bucketSize)
          .map((item) => `id:${item.id}`)
          .join(' OR ')
    );

    return idBuckets;
  }

  async rerenderFavorites(startIndex, endIndex, clear) {
    this._activeRequestController?.abort();
    const controller = new AbortController();
    this._activeRequestController = controller;
    const { signal } = controller;

    const currentHeight = this.gridElement.offsetHeight;
    this.gridElement.style.height = `${currentHeight}px`;
    this.gridElement.classList.add('favorites__grid--height-locked');
    this.gridElement.offsetHeight;

    const favorites = this.getFavoritesToShow();

    // Check cache first
    const cachedData = this.getCachedData(startIndex, endIndex, favorites);
    if (cachedData) {
      if (clear) {
        this.gridElement.innerHTML = '';
      }
      this.appendContent(cachedData);
      return true;
    }

    const queryStrings = this.getSearchQueryStrings(startIndex, endIndex);

    if (clear) {
      this.gridElement.innerHTML = '';
    }

    try {
      const promises = queryStrings.map((string) =>
        fetch(`${window.routes.localeRootUrl}/search?view=favorites&type=product&q=${string}`, {
          credentials: "same-origin",
          method: "GET",
          signal,
        })
      );

      const responses = await Promise.all(promises);
      if (signal.aborted) return;
      const htmls = await Promise.all(responses.map(r => {
        if (!r.ok) throw new Error(`Favorites search ${r.status}`);
        return r.text();
      }));
      if (signal.aborted) return;
      const combinedHtml = htmls.join('');

      this.setCachedData(startIndex, endIndex, favorites, combinedHtml);
      this.appendContent(combinedHtml);

    } catch (error) {
      if (error.name === 'AbortError') return;
      console.error('Error fetching favorites:', error);
      this._resetLoadingState();
    }

    return true;
  }

  _resetLoadingState() {
    this.spinningLoader.classList.remove('is-visible');
    const buttonLoader = this.loadMoreButton?.querySelector('.itemLoader');
    if (buttonLoader) buttonLoader.classList.remove('is-visible');
    if (this.loadMoreButton) this.loadMoreButton.disabled = false;
    this.gridElement.style.height = '';
    this.gridElement.classList.remove('favorites__grid--height-locked');
  }

  appendContent(html) {
    this.spinningLoader.classList.remove('is-visible');
    const buttonLoader = this.loadMoreButton?.querySelector('.itemLoader');
    if (buttonLoader) buttonLoader.classList.remove('is-visible');
    if (this.loadMoreButton) this.loadMoreButton.disabled = false;

    const temp = document.createElement('div');
    temp.innerHTML = html;

    const contentElements = temp.querySelectorAll(
      '.Search--Result, .favorites__grid, [data-search-results]'
    );

    if (!contentElements.length) {
      console.error('Could not find content element in HTML response');
      return;
    }

    contentElements.forEach((el) => {
      while (el.firstElementChild) {
        this.gridElement.appendChild(el.firstElementChild);
      }
    });

    this.toggleFavorites();
    this.applyStoredSizes();
    this.updateLoadMoreButton();

    // timeout: 50ms tick lets the browser commit the new grid layout before releasing the locked height, preventing a 0-height flash. Cleared in disconnectedCallback.
    this._heightUnlockTimeout = setTimeout(() => {
      this.gridElement.style.height = '';
      this.gridElement.classList.remove('favorites__grid--height-locked');
    }, 50);
  }

  applyStoredSizes() {
    const favorites = this.getFavoritesToShow();
    favorites.forEach(fav => {
      if (!fav.variantId && !fav.size) return;
      const productCard = this.gridElement.querySelector(`product-card[data-product-id="${fav.id}"]`);
      if (!productCard) return;

      const dropdown = productCard.querySelector('.pc-favorites__size-dropdown');
      if (!dropdown) return;

      const items = Array.from(dropdown.querySelectorAll('.pc-favorites__size-dropdown-item'));
      const option =
        (fav.variantId && items.find(el => el.dataset.variantId === String(fav.variantId))) ||
        (fav.size && items.find(el => el.dataset.size === fav.size));
      if (!option) return;

      this._applySizeToCard(productCard, option, { animate: false });
    });
  }

  async copyUrlToClipboard() {
    if (!this.disableShowingAddedProducts) {
      this.updateUrl();
    }

    const url = window.location.href;

    const urlObj = new URL(url);
    const favoritesParam = urlObj.searchParams.get('favorites');

    if (!favoritesParam && this.getFavorites().length > 0) {
      console.warn(
        'URL does not contain favorites data - sharing may not work properly'
      );
    }

    try {
      await navigator.clipboard.writeText(url);
      this.showSnackbar('copy');
    } catch (error) {
      this.fallbackCopyToClipboard(url);
    }
  }

  fallbackCopyToClipboard(text) {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.left = '-999999px';
    textArea.style.top = '-999999px';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();

    try {
      document.execCommand('copy');
      this.showSnackbar('copy');
    } catch (err) {
      console.error('Fallback copy failed:', err);
    }

    document.body.removeChild(textArea);
  }

  showSnackbar(identifier) {
    this.snackbar = document.querySelector(
      `.favorites__snackbar--${identifier}`
    );
    if (!this.snackbar) return;
    this.snackbar.classList.add('is-visible');

    if (this._snackbarBodyClickHandler) {
      this.delegateBody.off('click', this._snackbarBodyClickHandler);
      this._snackbarBodyClickHandler = null;
    }

    const dismiss = () => {
      clearTimeout(this.snackbarTimeout);
      this.snackbar.classList.remove('is-visible');
      this.delegateBody.off('click', this._snackbarBodyClickHandler);
      this._snackbarBodyClickHandler = null;
    };

    // Defer registration past the current tap. On iOS WebKit, navigator.clipboard.writeText
    // resolves inside the microtask checkpoint of the originating click, so the click then
    // bubbles to <body> AFTER showSnackbar — registering synchronously dismisses immediately.
    this._snackbarRegisterRaf = requestAnimationFrame(() => {
      this._snackbarRegisterRaf = null;
      this._snackbarBodyClickHandler = dismiss;
      this.delegateBody.on('click', this._snackbarBodyClickHandler);
    });

    if (this.snackbarTimeout) {
      clearTimeout(this.snackbarTimeout);
    }

    // timeout: 3s auto-dismiss for the share-link snackbar. Cleared in disconnectedCallback and re-cleared on each new showSnackbar() call above.
    this.snackbarTimeout = setTimeout(() => {
      this.snackbar.classList.remove('is-visible');
    }, 3000);
  }
}

customElements.define('favorites-section', Favorites);